import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { services } from '../data/mock';
import { ArrowLeft, ArrowRight, Check, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import Navbar from './Navbar';
import { Footer } from './Contact';

const serviceFeatures = {
  1: [
    { title: 'Off-Market Opportunities', desc: 'Exclusive access to investment deals not available through public channels, providing unique advantages for discerning investors.' },
    { title: 'Strategic Capital Deployment', desc: 'Carefully optimized allocation strategies designed to maximize risk-adjusted returns across market cycles.' },
    { title: 'Rigorous Due Diligence', desc: 'Comprehensive analysis and verification processes ensuring informed decision-making before any capital commitment.' },
    { title: 'Industry Partnerships', desc: 'Established relationships with leading non-banking financial institutions and premier credit fund managers globally.' }
  ],
  2: [
    { title: 'High-Demand Sectors', desc: 'Strategic focus on emerging and underserved healthcare needs across Australia and the broader Asia-Pacific region.' },
    { title: 'Stable Cash Flow', desc: 'Investment structures meticulously designed to deliver consistent returns exceeding market benchmarks.' },
    { title: 'Patient-Centric Approach', desc: 'Investments that enhance service delivery, improve patient outcomes, and drive sustainable healthcare innovation.' },
    { title: 'Premier Partnerships', desc: "Deep collaboration with Australia's foremost healthcare service providers, care organizations, and medical institutions." }
  ],
  3: [
    { title: 'Scalable Projects', desc: 'Investment opportunities ranging from megawatt-scale installations to multi-gigawatt utility-scale developments.' },
    { title: 'Global Partnerships', desc: 'Strategic alliances with world-leading renewable energy manufacturers, operators, and technical advisors.' },
    { title: 'Full Lifecycle Management', desc: 'Comprehensive oversight from early-stage development approvals through to full commercial operation.' },
    { title: 'Regional Expertise', desc: 'Deep market knowledge and operational excellence across Oceania and Indian renewable energy markets.' }
  ],
  4: [
    { title: 'Strategic Land Acquisition', desc: 'Identification and acquisition of high-quality agricultural land positioned in strategic growth corridors.' },
    { title: 'Rezoning Opportunities', desc: 'Long-term value creation through disciplined land banking and strategic development positioning strategies.' },
    { title: 'Sustainable Practices', desc: 'Commitment to sustainable farming operations ensuring environmental stewardship and long-term value preservation.' },
    { title: 'Urban Growth Capture', desc: 'Strategically positioned to benefit from urban expansion, infrastructure development, and rezoning opportunities.' }
  ],
  5: [
    { title: 'Prime Locations', desc: 'Focus on commercial, residential, and mixed-use properties in premium markets across three continents.' },
    { title: 'Value-Add Initiatives', desc: 'Strategic repositioning, renovation, and enhancement programs designed to deliver superior risk-adjusted returns.' },
    { title: 'Active Asset Management', desc: 'Hands-on management approach ensuring optimal property performance, tenant satisfaction, and value creation.' },
    { title: 'Multi-Region Portfolio', desc: 'Diversified holdings across India, Asia, and Australia providing balanced exposure and risk mitigation.' }
  ],
  6: [
    { title: 'Exclusive Access to Unicorns', desc: 'Direct allocation to late-stage funding rounds in category-defining companies like SpaceX, Stripe, Epic Games, and other billion-dollar valuations.' },
    { title: 'Institutional-Grade Deal Flow', desc: 'Access to proprietary private equity opportunities typically reserved for the world\'s most sophisticated institutional investors.' },
    { title: 'Global Network Advantage', desc: 'Leverage relationships with leading investment banks, family offices, and sovereign wealth funds for unmatched deal sourcing.' },
    { title: 'Multi-Sector Expertise', desc: 'Deep operational experience across technology, manufacturing, consumer goods, financial services, and infrastructure sectors.' }
  ],
  7: [
    { title: 'World-Class AI Investments', desc: 'Allocation to breakthrough companies like Anthropic (Claude AI), xAI, Neuralink, and OpenAI reshaping the future of technology.' },
    { title: 'Frontier Technology Focus', desc: 'Strategic investments in artificial intelligence, quantum computing, biotechnology, and next-generation enterprise software.' },
    { title: 'Institutional Co-Investment', desc: 'Access to oversubscribed funding rounds through partnerships with top-tier venture capital firms and strategic investors.' },
    { title: 'Value-Add Partnership', desc: 'Beyond capital: strategic mentorship, network access, and operational guidance from seasoned technology investors and entrepreneurs.' }
  ]
};

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  
  const currentIndex = services.findIndex(s => s.id === parseInt(serviceId));
  const service = services[currentIndex];
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceId]);

  if (!service) {
    return (
      <div className="min-h-screen bg-[#0a1220] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-3xl text-white mb-6">Service Not Found</h1>
          <Button onClick={() => navigate('/')} className="bg-[#b4975a] hover:bg-[#9a7d45] text-[#0a1220]">
            Return Home
          </Button>
        </div>
      </div>
    );
  }

  const features = serviceFeatures[service.id];
  const prevService = currentIndex > 0 ? services[currentIndex - 1] : null;
  const nextService = currentIndex < services.length - 1 ? services[currentIndex + 1] : null;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105"
          style={{ backgroundImage: `url('${service.image}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1220]/95 via-[#0a1220]/90 to-[#0a1220]/70" />
        
        {/* Top Gold Line */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#b4975a] to-transparent" />
        
        {/* Decorative Lines */}
        <div className="absolute bottom-0 left-0 w-1/3 h-[1px] bg-[#b4975a]" />
        <div className="absolute top-1/3 right-0 w-32 h-[1px] bg-[#b4975a]/30" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-40">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-3 text-[#b4975a] hover:text-white mb-16 transition-colors text-xs tracking-[0.2em] uppercase"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Return to Home</span>
          </button>
          
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              {/* Service Number */}
              <div className="flex items-center gap-6 mb-10">
                <span className="text-[#b4975a] text-8xl font-serif font-light opacity-50">0{service.id}</span>
                <div className="w-24 h-[1px] bg-[#b4975a]" />
              </div>
              
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-10 leading-[1.1]">
                {service.title}
              </h1>
              
              <p className="text-xl text-white/60 leading-relaxed mb-12">
                {service.description}
              </p>
              
              <Button
                onClick={() => {
                  navigate('/');
                  setTimeout(() => {
                    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="bg-[#b4975a] hover:bg-[#9a7d45] text-[#0a1220] px-12 py-7 text-xs font-semibold tracking-[0.2em] uppercase transition-all duration-500"
              >
                Discuss This Opportunity
                <ChevronRight className="ml-3 w-4 h-4" />
              </Button>
            </div>
            
            {/* Investment Highlights Card */}
            <div className="hidden lg:block">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-12">
                <h3 className="text-[#b4975a] text-xs tracking-[0.3em] uppercase mb-10">Investment Highlights</h3>
                <div className="space-y-8">
                  {features.slice(0, 3).map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-5">
                      <div className="w-10 h-10 bg-[#b4975a]/10 flex items-center justify-center flex-shrink-0 mt-1">
                        <Check className="w-5 h-5 text-[#b4975a]" />
                      </div>
                      <div>
                        <h4 className="text-white font-medium mb-2">{feature.title}</h4>
                        <p className="text-white/40 text-sm leading-relaxed">{feature.desc.slice(0, 80)}...</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32 lg:py-40 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="flex items-center justify-center gap-6 mb-8">
              <div className="w-16 h-[1px] bg-[#b4975a]" />
              <span className="text-[#b4975a] text-xs font-medium tracking-[0.3em] uppercase">Why Choose Us</span>
              <div className="w-16 h-[1px] bg-[#b4975a]" />
            </div>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#0f1c2e] mb-6">
              Key Advantages
            </h2>
            <p className="text-[#0f1c2e]/50 max-w-2xl mx-auto leading-relaxed">
              Discover why sophisticated investors partner with Rosevilla Group for {service.title.toLowerCase()}.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="group relative bg-white p-10 lg:p-12 border-l-2 border-[#b4975a] hover:shadow-2xl transition-all duration-500"
              >
                <div className="absolute top-10 right-10 text-7xl font-serif text-[#0f1c2e]/5 group-hover:text-[#b4975a]/10 transition-colors duration-500">
                  0{idx + 1}
                </div>
                <h3 className="text-[#0f1c2e] font-semibold text-xl mb-4 relative">{feature.title}</h3>
                <p className="text-[#0f1c2e]/50 leading-relaxed relative">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-32 lg:py-40 bg-[#0f1c2e]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <div className="flex items-center gap-6 mb-8">
                <div className="w-16 h-[1px] bg-[#b4975a]" />
                <span className="text-[#b4975a] text-xs font-medium tracking-[0.3em] uppercase">Our Process</span>
              </div>
              <h2 className="font-serif text-4xl lg:text-5xl text-white mb-10">
                How We Deliver<br />Excellence
              </h2>
              <p className="text-white/50 mb-12 leading-relaxed">
                Our disciplined investment approach combines rigorous analysis with creative structuring to maximize value while prudently managing risk throughout the investment lifecycle.
              </p>
              
              <div className="space-y-8">
                {[
                  { num: '01', title: 'Analysis', desc: 'Comprehensive market analysis and rigorous due diligence' },
                  { num: '02', title: 'Structuring', desc: 'Creative deal structuring for optimal risk-adjusted returns' },
                  { num: '03', title: 'Management', desc: 'Active hands-on management throughout the investment lifecycle' },
                  { num: '04', title: 'Exit', desc: 'Strategic exit planning to maximize value realization' }
                ].map((step, idx) => (
                  <div key={idx} className="flex items-start gap-8">
                    <div className="text-[#b4975a] font-serif text-3xl font-light">{step.num}</div>
                    <div>
                      <h4 className="text-white font-semibold mb-2">{step.title}</h4>
                      <p className="text-white/40 text-sm">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-[550px] object-cover"
              />
              {/* Decorative Frame */}
              <div className="absolute -bottom-6 -left-6 w-full h-full border border-[#b4975a]/30 pointer-events-none" />
              <div className="absolute -top-6 -right-6 w-32 h-32 border-t border-r border-[#b4975a]" />
              
              {/* Stats Overlay */}
              <div className="absolute bottom-0 right-0 bg-[#b4975a] p-10">
                <div className="text-[#0a1220] font-serif text-5xl font-light mb-1">$4B<span className="text-2xl">+</span></div>
                <div className="text-[#0a1220]/60 text-xs tracking-[0.15em] uppercase">Collectively Deployed</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 lg:py-40 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-6 mb-8">
            <div className="w-16 h-[1px] bg-[#b4975a]" />
            <span className="text-[#b4975a] text-xs font-medium tracking-[0.3em] uppercase">Get Started</span>
            <div className="w-16 h-[1px] bg-[#b4975a]" />
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl text-[#0f1c2e] mb-8">
            Ready to Explore<br />{service.title}?
          </h2>
          <p className="text-[#0f1c2e]/50 mb-12 max-w-2xl mx-auto leading-relaxed">
            Connect with our experienced team to discuss how we can help you achieve your investment objectives with confidence.
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center">
            <Button
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
              className="bg-[#0f1c2e] hover:bg-[#1a2d47] text-white px-12 py-7 text-xs font-semibold tracking-[0.2em] uppercase transition-all duration-500"
            >
              Contact Our Team
            </Button>
            <Button
              onClick={() => navigate('/#services')}
              variant="outline"
              className="border-[#0f1c2e] text-[#0f1c2e] hover:bg-[#0f1c2e] hover:text-white px-12 py-7 text-xs font-semibold tracking-[0.2em] uppercase transition-all duration-500"
            >
              View All Services
            </Button>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-16 bg-[#fafafa] border-t border-[#0f1c2e]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {prevService ? (
              <button
                onClick={() => navigate(`/services/${prevService.id}`)}
                className="group flex items-center gap-8 text-left hover:bg-white p-6 -m-6 transition-all duration-500"
              >
                <div className="w-14 h-14 border border-[#0f1c2e]/20 flex items-center justify-center group-hover:border-[#b4975a] group-hover:bg-[#b4975a] transition-all duration-500">
                  <ArrowLeft className="w-5 h-5 text-[#0f1c2e]/40 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <div className="text-[10px] text-[#0f1c2e]/40 tracking-[0.2em] uppercase mb-2">Previous</div>
                  <div className="text-[#0f1c2e] font-medium">{prevService.title}</div>
                </div>
              </button>
            ) : <div />}
            
            {nextService ? (
              <button
                onClick={() => navigate(`/services/${nextService.id}`)}
                className="group flex items-center gap-8 text-right hover:bg-white p-6 -m-6 transition-all duration-500"
              >
                <div>
                  <div className="text-[10px] text-[#0f1c2e]/40 tracking-[0.2em] uppercase mb-2">Next</div>
                  <div className="text-[#0f1c2e] font-medium">{nextService.title}</div>
                </div>
                <div className="w-14 h-14 border border-[#0f1c2e]/20 flex items-center justify-center group-hover:border-[#b4975a] group-hover:bg-[#b4975a] transition-all duration-500">
                  <ArrowRight className="w-5 h-5 text-[#0f1c2e]/40 group-hover:text-white transition-colors" />
                </div>
              </button>
            ) : <div />}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceDetail;
